import { AssetSource, IntegrationInstance } from 'models'
import { sanitizeAssetValue } from 'lib/sanitizeAssetValue'
import { generateRowUrl } from 'lib/generateRowUrl'

/**
 * The product slug follows the icon naming convention, so it doubles as the logo key and the
 * label is the same slug made readable.
 */
export const productTitle = (slug: string): string => {
  const clean = sanitizeAssetValue(slug)

  if (!clean) return ''

  return clean
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * An instance names itself where it was labelled. Otherwise its raw name embeds the product slug,
 * so only the part that tells two instances of one product apart is kept.
 */
export const sourceLabel = (source: AssetSource): string => {
  if (source.displayName) return source.displayName

  const product = productTitle(source.product)

  if (!source.instance || source.instance === source.product) return product

  const suffix = source.instance.startsWith(`${source.product}_`)
    ? source.instance.slice(source.product.length + 1)
    : source.instance

  return suffix ? `${product} · ${suffix}` : product
}

/* Older builds report a row's sources as bare product slugs, with no instance behind them. */
export const toProductSource = (slug: string): AssetSource => ({
  product: slug,
  instance: slug,
  displayName: null
})

const memberInstance = (member: Record<string, unknown>): unknown =>
  member.ONYXIA_PRODUCT_INSTANCE ?? member.instance_name

/**
 * A link per reporting instance, built from one of that instance's member records and the
 * instance's own URI template. Instances without a template, or whose template resolves to
 * something other than a web address, get no link.
 */
export const originUrls = (
  sources: AssetSource[],
  members: Record<string, unknown>[],
  instances: IntegrationInstance[]
): Record<string, string> => {
  const urls: Record<string, string> = {}

  for (const source of sources) {
    const member = members.find((item) => memberInstance(item) === source.instance)

    if (!member) continue

    const url = generateRowUrl({ ...member, ONYXIA_PRODUCT_INSTANCE: source.instance }, instances)

    if (url && /^https?:\/\//i.test(url)) urls[source.instance] = url
  }

  return urls
}
